import { Typography } from '@mui/material';
import moment from 'moment/moment';
import React, { FC, ReactElement, useCallback, useMemo } from 'react';
import Countdown, { CountdownRenderProps } from 'react-countdown';
import { usePadStart } from 'shared/hooks';

interface BreakTimerProps {
  startedAt: string;
  duration: number; // the break duration in seconds
  prefix?: string;
  completedState?: ReactElement;
}

export const BreakTimer: FC<BreakTimerProps> = ({
  startedAt,
  prefix = 'Break',
  completedState,
  duration,
}) => {
  const { padStart } = usePadStart();

  const date = useMemo(() => {
    if (!startedAt) {
      return null;
    }

    const time = moment(startedAt).add(duration, 'seconds');
    if (time.isBefore(moment())) {
      return null;
    }
    return time;
  }, [startedAt, duration]);

  const updateTimeHandler = useCallback(
    (props: CountdownRenderProps) => {
      if (!props.total) {
        return completedState ?? null;
      }

      const { minutes, seconds } = props;
      let content = '';

      if (minutes >= 1) {
        content = `${prefix} ${padStart(minutes)}m ${padStart(seconds)}s`;
      } else if (!minutes && seconds) {
        content = `${prefix} ${padStart(seconds)}s`;
      }

      return (
        <Typography component="span" display="inline">
          {content}
        </Typography>
      );
    },
    [completedState, prefix, padStart]
  );

  if (!date) {
    return completedState ?? null;
  }

  return <Countdown date={date?.toISOString()} renderer={updateTimeHandler} />;
};
